import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SalaryService } from '../services/salary.service';
import type { UserSalary, SalaryFilters } from '../types/salary.types';
import { useState } from 'react';
import { toast } from 'sonner';

export function useSalaries(companyId: string, initialFilters: SalaryFilters = {}) {
  const [filters, setFilters] = useState<SalaryFilters>(initialFilters);

  const query = useQuery({
    queryKey: ['salaries', companyId, filters],
    queryFn: () => SalaryService.getSalaries(companyId, filters),
    enabled: !!companyId,
  });

  return {
    ...query,
    filters,
    setFilters
  };
}

export function useUserSalaries(userId?: string) {
  return useQuery({
    queryKey: ['user-salaries', userId],
    queryFn: () => SalaryService.getUserSalaries(userId!),
    enabled: !!userId,
  });
}

export function useMutateSalary() {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (salary: Partial<UserSalary>) => SalaryService.createSalary(salary),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['salaries'] });
      if (variables.user_id) {
        queryClient.invalidateQueries({ queryKey: ['user-salaries', variables.user_id] });
      }
      toast.success('Đã cấu hình lương thành công');
    },
    onError: (error: any) => {
      toast.error('Lỗi khi cấu hình lương: ' + error.message);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<UserSalary> }) => 
      SalaryService.updateSalary(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salaries'] });
      queryClient.invalidateQueries({ queryKey: ['user-salaries'] });
      toast.success('Đã cập nhật lương thành công');
    },
    onError: (error: any) => {
      toast.error('Lỗi khi cập nhật lương: ' + error.message);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => SalaryService.deleteSalary(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salaries'] });
      queryClient.invalidateQueries({ queryKey: ['user-salaries'] });
      toast.success('Đã xóa cấu hình lương thành công');
    },
    onError: (error: any) => {
      toast.error('Lỗi khi xóa cấu hình lương: ' + error.message);
    }
  });

  return {
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    remove: deleteMutation.mutateAsync,
    isPending: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending
  };
}
